import * as swcHelpers from '@swc/helpers';
import { ApolloLink, HttpLink, ApolloClient, from, InMemoryCache, useLazyQuery as useLazyQuery$1, useMutation as useMutation$1, useQuery as useQuery$1 } from '@apollo/client';
export { ApolloConsumer, ApolloError, ApolloProvider, InMemoryCache, NetworkStatus, defaultDataIdFromObject, gql, makeVar, useApolloClient, useReactiveVar, useSubscription } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import ApolloLinkTimeout from 'apollo-link-timeout';
import * as React from 'react';
import { ApolloLink as ApolloLink$1 } from '@apollo/client/link/core';
import { MockLink, MockedProvider } from '@apollo/client/testing';
export { MockedProvider } from '@apollo/client/testing';
export * from 'graphql';

// TODO: fix/improve token caching
// This is a hardcoded map of the queries and mutations that require authentication, when this
// map isn't in place, every single graphQL request will also send a request to
// check the session of auth0, this is a naive way to skip over all the graphQL
// queries which we know don't require authentication
const authOperations = new Set([
    'CancelSubscription',
    'ChangeSubscription',
    'CheckCustomerConsents',
    'CreateEyeExam',
    'CreateStoredCardPayment',
    'CreateStoredSepaPaymentMethod',
    'findSubscription',
    'getSuspendSubscription',
    'getBillingAddress',
    'getChangeSubscriptionPreview',
    'getCurrentSubscriptionDetails',
    'getOptIns',
    'getPersonalDetails',
    'getPreferredStore',
    'getRecentOrders',
    'getPrescriptionDownloads',
    'getAppointmentById',
    'getMyAppointments',
    'getMySubscriptions',
    'getOrderDetails',
    'getMyOrderHistory',
    'getSubscriptionDeliveries',
    'getSubscriptionPayments',
    'getSubscriptionPrescription',
    'getSubscriptionPrescriptionProducts',
    'getSubscriptionNotifications',
    'getSubscriptionProductRateplans',
    'getSuspensionResumeDates',
    'previewChangeSubscription',
    'ResumeSubscription',
    'RetrySubscriptionPayment',
    'SaveNewConsent',
    'SuspendSubscription',
    'UpdateBillingAddress',
    'UpdateDeliveryAddress',
    'updateOptIns',
    'UpdatePersonalDetails',
    'UpdateSubscriptionBillingAddress',
    'UpdateSubscriptionPrescription',
    'storedPaymentMethods',
    'ChangePaymentMethod',
    'completeChallengeFlowStoredPayment',
    'completeSocialLogin',
    'SocialLoginMerge', 
]);
const authCallback = (options)=>(operation, previousRequest)=>new Promise(function() {
            var _ref = swcHelpers.asyncToGenerator(function*(resolve, reject) {
                // Only continue if the operationName is one that we know requires authentication
                const requiresAuth = operation.operationName && authOperations.has(operation.operationName);
                if (!requiresAuth || !options.getToken) return resolve(previousRequest);
                try {
                    const token = yield options.getToken();
                    resolve({
                        headers: swcHelpers.objectSpread({}, previousRequest.headers, {
                            authorization: `Bearer ${token}`
                        })
                    });
                } catch (e) {
                    reject(e);
                }
            });
            return function(resolve, reject) {
                return _ref.apply(this, arguments);
            };
        }())
;
const authLink = (options)=>setContext(authCallback(options))
;
const httpLink = (options)=>new HttpLink(swcHelpers.objectSpread({
        credentials: 'same-origin',
        fetch
    }, options))
;
const errorLink = onError(({ graphQLErrors , networkError  })=>{
    if (graphQLErrors) {
        graphQLErrors.forEach((error)=>console.error(`[GraphQL error]: ${error.message}`)
        );
    }
    if (networkError) {
        console.error('[Network error]:', networkError);
    }
});
const forwardHeaders = (operation, forward)=>{
    const context = operation.getContext();
    const forwardHeaders1 = context.forwardHeaders;
    operation.setContext(({ headers ={}  })=>({
            headers: swcHelpers.objectSpread({}, headers, forwardHeaders1 && forwardHeaders1)
        })
    );
    return forward(operation);
};
const forwardHeadersLink = new ApolloLink(forwardHeaders);
const timeoutLink = (timeoutMs, statusCode)=>new ApolloLinkTimeout(timeoutMs, statusCode)
;

const wait = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(ms) {
        return new Promise((resolve)=>setTimeout(resolve, ms)
        );
    });
    return function wait(ms) {
        return _ref.apply(this, arguments);
    };
}();
// Find the given errorCode, when facing an internal server error, the errorcode can be found within the original error
const getGraphQLErrorCode = (error1, errorCode)=>{
    return error1.graphQLErrors.find((error)=>{
        var ref, ref1, ref2, ref3;
        return ((ref = error.extensions) === null || ref === void 0 ? void 0 : ref.code) === errorCode || ((ref2 = (ref1 = error.extensions) === null || ref1 === void 0 ? void 0 : ref1.exception.originalError) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.extensions) === null || ref3 === void 0 ? void 0 : ref3.code) === errorCode;
    });
};
// Formats key-value pairs of versions:
// {
//  core: 'v100.0.0',
//  gb-visionexpress: 'v45.0.0',
//  ...
// }
const formatClientVersionInfo = (versions)=>{
    return Object.entries(versions || {}).map((parts)=>parts.join('@')
    ) // parts = [name, version]
    .join('/');
};

let apolloClient;
const initApolloClient = (uri, options)=>{
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections
    if (typeof window === 'undefined') {
        return createApolloClient(uri, options);
    }
    if (!apolloClient) {
        apolloClient = createApolloClient(uri, options);
    }
    return apolloClient;
};
const createApolloClient = (uri, options = {})=>{
    var ref;
    if (!uri) {
        console.error('No graphQL endpoint specified.');
        throw new Error('No graphQL endpoint specified.');
    }
    const cacheConfig = options.cacheConfig || {};
    const initialState = options.initialState || {};
    const queryOptions = options.queryOptions || {};
    const headers = {};
    const persistedQueriesLink = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true
    });
    const clientInfo = options === null || options === void 0 ? void 0 : options.clientInfo;
    const name = ((ref = options.clientInfo) === null || ref === void 0 ? void 0 : ref.name) || '';
    const version = formatClientVersionInfo(clientInfo === null || clientInfo === void 0 ? void 0 : clientInfo.versions);
    return new ApolloClient({
        ssrMode: typeof window !== 'undefined',
        link: from([
            errorLink,
            timeoutLink(10000),
            forwardHeadersLink,
            authLink(options),
            ...queryOptions.isPersistentQueryDisabled ? [] : [
                persistedQueriesLink
            ],
            httpLink({
                headers,
                uri
            }), 
        ]),
        cache: new InMemoryCache(swcHelpers.objectSpread({
            typePolicies: {
                LegacyProduct: {
                    keyFields: [
                        'sku'
                    ]
                },
                Variant: {
                    keyFields: [
                        'gan'
                    ]
                },
                ContactLensVariant: {
                    keyFields: [
                        'gan'
                    ]
                },
                ContactLensProduct: {
                    keyFields: [
                        'productMasterKey'
                    ]
                },
                OnlineOrdersData: {
                    fields: {
                        results: {
                            merge (existing = [], incoming, { readField  }) {
                                if (!existing.length) {
                                    // Don't merge when `existing` is empty or when the `limit` variable is undefined.
                                    return incoming;
                                }
                                /**
                 * Merge paginated orders, used by the useGetMyOrderHistory. This function doesn't return duplicate orders.
                 * We have seen cases where multiple queries were called and duplicate orders were displayed.
                 */ const merged = [
                                    ...existing
                                ];
                                incoming.forEach((item)=>{
                                    const i = merged.findIndex((value)=>readField('orderNumber', value) === readField('orderNumber', item)
                                    );
                                    if (i === -1) {
                                        // Order is new
                                        merged.push(item);
                                    } else {
                                        // Order already exists
                                        merged[i] = item;
                                    }
                                });
                                return merged;
                            }
                        }
                    }
                },
                Order: {
                    // Cache orders based on the unique `orderNumber`
                    keyFields: [
                        'orderNumber'
                    ]
                },
                Customer: {
                    fields: {
                        orders: {
                            // Don't cache based on the variables `limit` and `offset`
                            keyArgs: false
                        }
                    }
                },
                SolutionVariant: {
                    keyFields: [
                        'gan'
                    ],
                    fields: {
                        product: {
                            // Custom merge function tells apollo its safe to merge SolutionProducts which are direct descendants of SolutionVariants, this effectively uses the parent SolutionVariant 'gan' as the unique identifier for SolutionProduct
                            // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
                            merge (existing, incoming) {
                                return swcHelpers.objectSpread({}, existing, incoming);
                            }
                        }
                    }
                }
            }
        }, cacheConfig)).restore(initialState),
        name,
        version
    });
};

var AccountValidationPayload = [
    "AccountValidatedSuccessfully",
    "AccountValidationMaxRetriesError"
];
var Address = [
    "BillingAddress",
    "DeliveryAddress"
];
var CompletedPaymentResultData = [
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var OrderLineItem = [
    "OrderLineItemBundle",
    "OrderLineItemContactLens",
    "OrderLineItemNonPrescriptionBundle",
    "OrderLineItemPrescriptionGlass",
    "OrderLineItemRegular",
    "OrderLineItemSolution",
    "OrderLineItemSubscription"
];
var Domain = [
    "ContactLensProduct",
    "FrameProduct",
    "SolutionProduct",
    "SunglassesProduct"
];
var IValidationError = [
    "SolvableValidationError",
    "ValidationError"
];
var LineItem = [
    "LineItemBundle",
    "LineItemContactLenses",
    "LineItemRegular"
];
var Location = [
    "PickupPoint",
    "Store"
];
var MutationPayload = [
    "CompletePaymentPayload",
    "CreateEyeExamPayload",
    "StoredPaymentMethodMutationPayload",
    "UpdateEyeExamPayload"
];
var MutationResponse = [
    "CartMutationResponse",
    "ShareCartMutationResponse",
    "TicketMutationResponse"
];
var OrderedBundledLineItem = [
    "OrderLineItemContactLens",
    "OrderLineItemSolution"
];
var OrderedProduct = [
    "OrderedProductContactLens",
    "OrderedProductContactLensBundle",
    "OrderedProductPrescriptionGlass",
    "OrderedProductRegular",
    "OrderedProductSolution",
    "OrderedProductSubscription"
];
var OrderedSubscriptionLineItem = [
    "OrderLineItemContactLens",
    "OrderLineItemSolution"
];
var PaymentTransactionMethod = [];
var Prescription = [
    "PrescriptionDownload"
];
var PrescriptionGlassPrescription = [
    "AddedGlassesPrescription",
    "EmailPrescription",
    "ExistingPrescription",
    "FilePrescription"
];
var Product = [
    "ContactLensProduct",
    "FrameProduct",
    "SolutionProduct",
    "SunglassesProduct"
];
var RatePlan = [
    "ContactLensRatePlan",
    "SolutionRatePlan"
];
var RecommendationBannerAction = [
    "RecommendationBannerApplyDiscountCodeAction",
    "RecommendationBannerRedirectToLinkAction"
];
var RecommendationProductAction = [
    "RecommendationProductAddToCartAction",
    "RecommendationProductRedirectAction"
];
var StoreGroupInterface = [
    "SingleStoreGroup",
    "StoreGroup"
];
var StoredPaymentMethod = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var StoredPaymentMethodResult = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var StoredPaymentResult = [
    "Payment3DSChallengeData",
    "PaymentRedirectData",
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var StoredPaymentResultData = [
    "Payment3DSChallengeData",
    "PaymentRedirectData",
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var SubscriptionProductVariant = [
    "ContactLensVariant",
    "SolutionVariant"
];
var SubscriptionStoredPaymentMethod = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var TypeDetails = [
    "BundleNonPrescriptionBundleTypeDetails",
    "BundlePrescriptionGlassesTypeDetails",
    "ContactLensBundleBeneluxTypeDetails",
    "ContactLensSubscriptionTypeDetails",
    "ContactLensesTypeDetails",
    "RegularTypeDetails"
];
var Variant = [
    "ContactLensVariant",
    "FrameVariant",
    "SolutionVariant",
    "SunglassesVariant"
];
var possibleTypes = {
    AccountValidationPayload: AccountValidationPayload,
    Address: Address,
    CompletedPaymentResultData: CompletedPaymentResultData,
    OrderLineItem: OrderLineItem,
    Domain: Domain,
    IValidationError: IValidationError,
    LineItem: LineItem,
    Location: Location,
    MutationPayload: MutationPayload,
    MutationResponse: MutationResponse,
    OrderedBundledLineItem: OrderedBundledLineItem,
    OrderedProduct: OrderedProduct,
    OrderedSubscriptionLineItem: OrderedSubscriptionLineItem,
    PaymentTransactionMethod: PaymentTransactionMethod,
    Prescription: Prescription,
    PrescriptionGlassPrescription: PrescriptionGlassPrescription,
    Product: Product,
    RatePlan: RatePlan,
    RecommendationBannerAction: RecommendationBannerAction,
    RecommendationProductAction: RecommendationProductAction,
    StoreGroupInterface: StoreGroupInterface,
    StoredPaymentMethod: StoredPaymentMethod,
    StoredPaymentMethodResult: StoredPaymentMethodResult,
    StoredPaymentResult: StoredPaymentResult,
    StoredPaymentResultData: StoredPaymentResultData,
    SubscriptionProductVariant: SubscriptionProductVariant,
    SubscriptionStoredPaymentMethod: SubscriptionStoredPaymentMethod,
    TypeDetails: TypeDetails,
    Variant: Variant
};

const DebugMockedProvider = (_param)=>{
    var { children , mocks , addPossibleTypes  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "mocks",
        "addPossibleTypes"
    ]);
    const mockLink = new MockLink(mocks || []);
    const link = ApolloLink$1.from([
        errorLink,
        mockLink
    ]);
    return /*#__PURE__*/ React.createElement(MockedProvider, swcHelpers.extends({
        cache: new InMemoryCache(addPossibleTypes ? {
            possibleTypes
        } : {}),
        addTypename: false,
        link: link
    }, rest), children);
};

var AccessoryType;
(function(AccessoryType) {
    AccessoryType["AUDIO_BATTERY"] = "AUDIO_BATTERY";
    AccessoryType["AUDIO_CLEANING"] = "AUDIO_CLEANING";
    AccessoryType["AUDIO_EARPLUG"] = "AUDIO_EARPLUG";
    AccessoryType["AUDIO_HEADSET"] = "AUDIO_HEADSET";
    AccessoryType["BLUEFILTER_MASK_CLIPS"] = "BLUEFILTER_MASK_CLIPS";
    AccessoryType["CASES"] = "CASES";
    AccessoryType["CHARMS"] = "CHARMS";
    AccessoryType["CLEANING_CLOTH"] = "CLEANING_CLOTH";
    AccessoryType["CLEANING_LIQUID"] = "CLEANING_LIQUID";
    AccessoryType["CORDS"] = "CORDS";
    AccessoryType["MAGNETS"] = "MAGNETS";
    AccessoryType["MISCELLANEOUS"] = "MISCELLANEOUS";
    AccessoryType["NIGHTDRIVE_MASK_CLIPS"] = "NIGHTDRIVE_MASK_CLIPS";
    AccessoryType["SKI_GOGGLES"] = "SKI_GOGGLES";
    AccessoryType["SLIDERS"] = "SLIDERS";
    AccessoryType["SUNCOVER"] = "SUNCOVER";
    AccessoryType["SUN_MASK_CLIPS"] = "SUN_MASK_CLIPS";
    AccessoryType["SWIMMING_GOGGLES"] = "SWIMMING_GOGGLES";
})(AccessoryType || (AccessoryType = {}));
var AccountOrigination;
(function(AccountOrigination) {
    AccountOrigination["IN_STORE"] = "IN_STORE";
    AccountOrigination["ONLINE"] = "ONLINE";
})(AccountOrigination || (AccountOrigination = {}));
var AccountReason;
(function(AccountReason) {
    AccountReason["APPOINTMENT"] = "APPOINTMENT";
    AccountReason["ORDER"] = "ORDER";
    AccountReason["SIGN_UP"] = "SIGN_UP";
    AccountReason["SUBSCRIPTION"] = "SUBSCRIPTION";
})(AccountReason || (AccountReason = {}));
var AccountVerificationMethod;
(function(AccountVerificationMethod) {
    AccountVerificationMethod["DATE_OF_BIRTH"] = "DATE_OF_BIRTH";
    AccountVerificationMethod["NONE"] = "NONE";
})(AccountVerificationMethod || (AccountVerificationMethod = {}));
var AddressType;
(function(AddressType) {
    AddressType["BILLING"] = "BILLING";
    AddressType["DELIVERY"] = "DELIVERY";
})(AddressType || (AddressType = {}));
var AppointmentCommunicationChannel;
(function(AppointmentCommunicationChannel) {
    AppointmentCommunicationChannel["EMAIL"] = "EMAIL";
    AppointmentCommunicationChannel["SMS"] = "SMS";
})(AppointmentCommunicationChannel || (AppointmentCommunicationChannel = {}));
var BillingFrequency;
(function(BillingFrequency) {
    BillingFrequency["MONTHLY"] = "MONTHLY";
    BillingFrequency["QUARTERLY"] = "QUARTERLY";
})(BillingFrequency || (BillingFrequency = {}));
var BundleLineItemProductType;
(function(BundleLineItemProductType) {
    BundleLineItemProductType["LENS"] = "LENS";
    BundleLineItemProductType["SOLUTION"] = "SOLUTION";
})(BundleLineItemProductType || (BundleLineItemProductType = {}));
var BundleLineItemType;
(function(BundleLineItemType) {
    BundleLineItemType["COMPOSITE"] = "COMPOSITE";
    BundleLineItemType["CONTACT_LENSES_BENELUX"] = "CONTACT_LENSES_BENELUX";
    BundleLineItemType["CONTACT_LENSES_SUBSCRIPTION"] = "CONTACT_LENSES_SUBSCRIPTION";
    BundleLineItemType["NON_PRESCRIPTION_BUNDLE"] = "NON_PRESCRIPTION_BUNDLE";
    BundleLineItemType["PRESCRIPTION_GLASSES"] = "PRESCRIPTION_GLASSES";
})(BundleLineItemType || (BundleLineItemType = {}));
var CLCareType;
(function(CLCareType) {
    CLCareType["CLEANER"] = "CLEANER";
    CLCareType["CLEANER_AND_CONDITIONER"] = "CLEANER_AND_CONDITIONER";
    CLCareType["CONDITIONER"] = "CONDITIONER";
    CLCareType["ENZYME_TABLET"] = "ENZYME_TABLET";
    CLCareType["MULTIPURPOSE"] = "MULTIPURPOSE";
    CLCareType["PEROXIDE"] = "PEROXIDE";
    CLCareType["RIGID_GAS_PERMIABLE"] = "RIGID_GAS_PERMIABLE";
    CLCareType["SALINE"] = "SALINE";
})(CLCareType || (CLCareType = {}));
var CacheControlScope;
(function(CacheControlScope) {
    CacheControlScope["PRIVATE"] = "PRIVATE";
    CacheControlScope["PUBLIC"] = "PUBLIC";
})(CacheControlScope || (CacheControlScope = {}));
var CartCustomerType;
(function(CartCustomerType) {
    CartCustomerType["B2B"] = "B2B";
    CartCustomerType["B2C"] = "B2C";
})(CartCustomerType || (CartCustomerType = {}));
var CartState;
(function(CartState) {
    CartState["Active"] = "Active";
    CartState["Merged"] = "Merged";
    CartState["Ordered"] = "Ordered";
})(CartState || (CartState = {}));
var Channel;
(function(Channel) {
    Channel[/** Customer service */ "CS"] = "CS";
    Channel[/** Ecommerce */ "ECOM"] = "ECOM";
})(Channel || (Channel = {}));
var ChannelAvailability;
(function(ChannelAvailability) {
    ChannelAvailability["IN_STORE_ONLY"] = "IN_STORE_ONLY";
    ChannelAvailability["ONLINE"] = "ONLINE";
})(ChannelAvailability || (ChannelAvailability = {}));
var ChannelType;
(function(ChannelType) {
    ChannelType["ONLINE"] = "ONLINE";
    ChannelType["STORE"] = "STORE";
})(ChannelType || (ChannelType = {}));
var CheckoutCustomerType;
(function(CheckoutCustomerType) {
    CheckoutCustomerType["B2B"] = "B2B";
    CheckoutCustomerType["B2C"] = "B2C";
})(CheckoutCustomerType || (CheckoutCustomerType = {}));
var CheckoutState;
(function(CheckoutState) {
    CheckoutState["Finalized"] = "Finalized";
    CheckoutState["InProgress"] = "InProgress";
    CheckoutState["ReadyToOrder"] = "ReadyToOrder";
})(CheckoutState || (CheckoutState = {}));
var ColorType;
(function(ColorType) {
    ColorType["SINGLE_GRADIENT"] = "SINGLE_GRADIENT";
    ColorType["SOLID"] = "SOLID";
})(ColorType || (ColorType = {}));
var CommunicationChannel;
(function(CommunicationChannel) {
    CommunicationChannel["EMAIL"] = "EMAIL";
    CommunicationChannel["SMS"] = "SMS";
})(CommunicationChannel || (CommunicationChannel = {}));
var CreateAccountTargetAction;
(function(CreateAccountTargetAction) {
    CreateAccountTargetAction["FORGOT_PASSWORD"] = "FORGOT_PASSWORD";
    CreateAccountTargetAction["SIGN_UP"] = "SIGN_UP";
})(CreateAccountTargetAction || (CreateAccountTargetAction = {}));
var CustomerType;
(function(CustomerType) {
    CustomerType["B2B"] = "B2B";
    CustomerType["B2C"] = "B2C";
})(CustomerType || (CustomerType = {}));
var DayOfWeek;
(function(DayOfWeek) {
    DayOfWeek["FRIDAY"] = "FRIDAY";
    DayOfWeek["MONDAY"] = "MONDAY";
    DayOfWeek["SATURDAY"] = "SATURDAY";
    DayOfWeek["SUNDAY"] = "SUNDAY";
    DayOfWeek["THURSDAY"] = "THURSDAY";
    DayOfWeek["TUESDAY"] = "TUESDAY";
    DayOfWeek["WEDNESDAY"] = "WEDNESDAY";
})(DayOfWeek || (DayOfWeek = {}));
var DeliveryFrequency;
(function(DeliveryFrequency) {
    DeliveryFrequency["MONTHLY"] = "MONTHLY";
    DeliveryFrequency["QUARTERLY"] = "QUARTERLY";
})(DeliveryFrequency || (DeliveryFrequency = {}));
var DeliveryStatus;
(function(DeliveryStatus) {
    DeliveryStatus[/** The shipment has been delivered	COMPLETE */ "DELIVERED"] = "DELIVERED";
    DeliveryStatus[/** The shipment has failed	e.g. LOST_IN_TRANSIT, CANNOT_BE_SOURCED, ... */ "FAILED_DELIVERY"] = "FAILED_DELIVERY";
    DeliveryStatus[/** @deprecated Replaced by SCHEDULED */ "PROCESSING"] = "PROCESSING";
    DeliveryStatus[/** The customer rejected the shipment */ "REJECTED_BY_CUSTOMER"] = "REJECTED_BY_CUSTOMER";
    DeliveryStatus[/** The shipment has been returned complete */ "RETURN_COMPLETE"] = "RETURN_COMPLETE";
    DeliveryStatus[/** The shipment is in process and due to be send */ "SCHEDULED"] = "SCHEDULED";
    DeliveryStatus[/** The shipment has been sent out */ "SHIPPED"] = "SHIPPED";
    DeliveryStatus[/** Fallback delivery status that cannot be mapped in zip/zuora */ "UNKNOWN"] = "UNKNOWN";
})(DeliveryStatus || (DeliveryStatus = {}));
var DiscountProductType;
(function(DiscountProductType) {
    DiscountProductType["CART_DISCOUNT"] = "CART_DISCOUNT";
    DiscountProductType["PRODUCT_DISCOUNT"] = "PRODUCT_DISCOUNT";
})(DiscountProductType || (DiscountProductType = {}));
var DiscountType;
(function(DiscountType) {
    DiscountType["ABSOLUTE"] = "ABSOLUTE";
    DiscountType["CartDiscount"] = "CartDiscount";
    DiscountType["ProductDiscount"] = "ProductDiscount";
    DiscountType["RELATIVE"] = "RELATIVE";
})(DiscountType || (DiscountType = {}));
var DiscountValueType;
(function(DiscountValueType) {
    DiscountValueType["ABSOLUTE"] = "ABSOLUTE";
    DiscountValueType["GIFT"] = "GIFT";
    DiscountValueType["RELATIVE"] = "RELATIVE";
})(DiscountValueType || (DiscountValueType = {}));
var Dominance;
(function(Dominance) {
    Dominance["D"] = "D";
    Dominance["N"] = "N";
})(Dominance || (Dominance = {}));
var EaseePrescriptionType;
(function(EaseePrescriptionType) {
    EaseePrescriptionType["CONTACT_LENS_PRESCRIPTION"] = "CONTACT_LENS_PRESCRIPTION";
    EaseePrescriptionType["HEARING_AID_PRESCRIPTION"] = "HEARING_AID_PRESCRIPTION";
    EaseePrescriptionType["PRESCRIPTION_GLASSES_PRESCRIPTION"] = "PRESCRIPTION_GLASSES_PRESCRIPTION";
})(EaseePrescriptionType || (EaseePrescriptionType = {}));
var EyeSide;
(function(EyeSide) {
    EyeSide["BOTH"] = "BOTH";
    EyeSide["LEFT"] = "LEFT";
    EyeSide["RIGHT"] = "RIGHT";
})(EyeSide || (EyeSide = {}));
var FaceShape;
(function(FaceShape) {
    FaceShape["DIAMOND"] = "DIAMOND";
    FaceShape["HEART"] = "HEART";
    FaceShape["OBLONG"] = "OBLONG";
    FaceShape["OVAL"] = "OVAL";
    FaceShape["RECTANGLE"] = "RECTANGLE";
    FaceShape["ROUND"] = "ROUND";
    FaceShape["SQUARE"] = "SQUARE";
    FaceShape["TRIANGLE"] = "TRIANGLE";
})(FaceShape || (FaceShape = {}));
var FilterFieldNames;
(function(FilterFieldNames) {
    FilterFieldNames["group"] = "group";
    FilterFieldNames["slug"] = "slug";
})(FilterFieldNames || (FilterFieldNames = {}));
var FrameShape;
(function(FrameShape) {
    FrameShape["ALMOND"] = "ALMOND";
    FrameShape["AVIATOR"] = "AVIATOR";
    FrameShape["CAT_EYE"] = "CAT_EYE";
    FrameShape["HEXAGONAL"] = "HEXAGONAL";
    FrameShape["IRREGULAR"] = "IRREGULAR";
    FrameShape["MASK"] = "MASK";
    FrameShape["OVAL"] = "OVAL";
    FrameShape["PANTO"] = "PANTO";
    FrameShape["RECATANGLE_SQUARE"] = "RECATANGLE_SQUARE";
    FrameShape["RECTANGLE"] = "RECTANGLE";
    FrameShape["ROUND"] = "ROUND";
    FrameShape["ROUND_OVAL"] = "ROUND_OVAL";
    FrameShape["SQUARE"] = "SQUARE";
    FrameShape["UNDEFINED"] = "UNDEFINED";
})(FrameShape || (FrameShape = {}));
var FrameSize;
(function(FrameSize) {
    FrameSize["EXTRA_SMALL"] = "EXTRA_SMALL";
    FrameSize["LARGE"] = "LARGE";
    FrameSize["MEDIUM"] = "MEDIUM";
    FrameSize["SMALL"] = "SMALL";
})(FrameSize || (FrameSize = {}));
var FrameType;
(function(FrameType) {
    FrameType["CLIPS"] = "CLIPS";
    FrameType["GOOGLE"] = "GOOGLE";
    FrameType["HALF_EYE"] = "HALF_EYE";
    FrameType["MASK"] = "MASK";
    FrameType["SAFETY"] = "SAFETY";
    FrameType["STANDARD"] = "STANDARD";
})(FrameType || (FrameType = {}));
var HttpMethod;
(function(HttpMethod) {
    HttpMethod["GET"] = "GET";
    HttpMethod["POST"] = "POST";
})(HttpMethod || (HttpMethod = {}));
var LineItemType;
(function(LineItemType) {
    LineItemType["BUNDLE"] = "BUNDLE";
    LineItemType["CONTACTLENSES"] = "CONTACTLENSES";
    LineItemType["REGULAR"] = "REGULAR";
})(LineItemType || (LineItemType = {}));
var MaterialGroup;
(function(MaterialGroup) {
    MaterialGroup["COMBI"] = "COMBI";
    MaterialGroup["HYBRID"] = "HYBRID";
    MaterialGroup["HYDROGEL"] = "HYDROGEL";
    MaterialGroup["METAL"] = "METAL";
    MaterialGroup["MINERAL"] = "MINERAL";
    MaterialGroup["ORGANIC"] = "ORGANIC";
    MaterialGroup["PLASTIC"] = "PLASTIC";
    MaterialGroup["RIGID_GAS_PERMIABLE"] = "RIGID_GAS_PERMIABLE";
    MaterialGroup["SILICONE_HYDROGEL"] = "SILICONE_HYDROGEL";
    MaterialGroup["UNKNOWN"] = "UNKNOWN";
})(MaterialGroup || (MaterialGroup = {}));
var NormalizePrice;
(function(NormalizePrice) {
    NormalizePrice["NONE"] = "NONE";
    NormalizePrice["PER_MONTH"] = "PER_MONTH";
    NormalizePrice["PER_QUARTER"] = "PER_QUARTER";
})(NormalizePrice || (NormalizePrice = {}));
var OfferingAction;
(function(OfferingAction) {
    OfferingAction["ADD_TO_CART"] = "ADD_TO_CART";
    OfferingAction["ADD_TO_CART_UNAVAILABLE"] = "ADD_TO_CART_UNAVAILABLE";
    OfferingAction["OPEN_CONFIGURATOR"] = "OPEN_CONFIGURATOR";
    OfferingAction["OPEN_CONFIGURATOR_UNAVAILABLE"] = "OPEN_CONFIGURATOR_UNAVAILABLE";
})(OfferingAction || (OfferingAction = {}));
var OrderDeliveryMethod;
(function(OrderDeliveryMethod) {
    OrderDeliveryMethod["HOME_DELIVERY"] = "HOME_DELIVERY";
    OrderDeliveryMethod["PICK_UP"] = "PICK_UP";
})(OrderDeliveryMethod || (OrderDeliveryMethod = {}));
var OrderPaymentMethod;
(function(OrderPaymentMethod) {
    OrderPaymentMethod["APPLE_PAY"] = "APPLE_PAY";
    OrderPaymentMethod["BANCONTACT_CARD"] = "BANCONTACT_CARD";
    OrderPaymentMethod["BANCONTACT_MOBILE"] = "BANCONTACT_MOBILE";
    OrderPaymentMethod["CARD"] = "CARD";
    OrderPaymentMethod["CASH_ON_DELIVERY"] = "CASH_ON_DELIVERY";
    OrderPaymentMethod["FAKE"] = "FAKE";
    OrderPaymentMethod["IDEAL"] = "IDEAL";
    OrderPaymentMethod["KLARNA"] = "KLARNA";
    OrderPaymentMethod["KLARNA_ACCOUNT"] = "KLARNA_ACCOUNT";
    OrderPaymentMethod["MBWAY"] = "MBWAY";
    OrderPaymentMethod["MOBILEPAY"] = "MOBILEPAY";
    OrderPaymentMethod["MULTIBANCO"] = "MULTIBANCO";
    OrderPaymentMethod["ONEY"] = "ONEY";
    OrderPaymentMethod["ONEY_3X"] = "ONEY_3X";
    OrderPaymentMethod["ONEY_4X"] = "ONEY_4X";
    OrderPaymentMethod["PAYPAL"] = "PAYPAL";
    OrderPaymentMethod["SEPA"] = "SEPA";
    OrderPaymentMethod["SWISH"] = "SWISH";
})(OrderPaymentMethod || (OrderPaymentMethod = {}));
var OrderPaymentStatus;
(function(OrderPaymentStatus) {
    OrderPaymentStatus["BALANCE_DUE"] = "BALANCE_DUE";
    OrderPaymentStatus["CREDIT_OWED"] = "CREDIT_OWED";
    OrderPaymentStatus["FAILED"] = "FAILED";
    OrderPaymentStatus["PAID"] = "PAID";
    OrderPaymentStatus["PENDING"] = "PENDING";
})(OrderPaymentStatus || (OrderPaymentStatus = {}));
var OrderShipmentStatus;
(function(OrderShipmentStatus) {
    OrderShipmentStatus["BACK_ORDER"] = "BACK_ORDER";
    OrderShipmentStatus["DELAYED"] = "DELAYED";
    OrderShipmentStatus["PARTIAL"] = "PARTIAL";
    OrderShipmentStatus["PENDING"] = "PENDING";
    OrderShipmentStatus["READY"] = "READY";
    OrderShipmentStatus["SHIPPED"] = "SHIPPED";
})(OrderShipmentStatus || (OrderShipmentStatus = {}));
var OrderStatus;
(function(OrderStatus) {
    OrderStatus["CREATED"] = "CREATED";
    OrderStatus["PAID"] = "PAID";
})(OrderStatus || (OrderStatus = {}));
var PSP;
(function(PSP) {
    PSP["ADYEN"] = "ADYEN";
    PSP["CASH_ON_DELIVERY"] = "CASH_ON_DELIVERY";
    PSP["FAKE"] = "FAKE";
})(PSP || (PSP = {}));
var PaymentMethod;
(function(PaymentMethod) {
    PaymentMethod["APPLE_PAY"] = "APPLE_PAY";
    PaymentMethod["BANCONTACT_CARD"] = "BANCONTACT_CARD";
    PaymentMethod["BANCONTACT_MOBILE"] = "BANCONTACT_MOBILE";
    PaymentMethod["CASH_ON_DELIVERY"] = "CASH_ON_DELIVERY";
    PaymentMethod["CREDITCARD"] = "CREDITCARD";
    PaymentMethod["FAKE"] = "FAKE";
    PaymentMethod["IDEAL"] = "IDEAL";
    PaymentMethod["KLARNA"] = "KLARNA";
    PaymentMethod["KLARNA_ACCOUNT"] = "KLARNA_ACCOUNT";
    PaymentMethod["MBWAY"] = "MBWAY";
    PaymentMethod["MOBILE_PAY"] = "MOBILE_PAY";
    PaymentMethod["MULTIBANCO"] = "MULTIBANCO";
    PaymentMethod["ONEY"] = "ONEY";
    PaymentMethod["PAYPAL"] = "PAYPAL";
    PaymentMethod["SEPA_DIRECT_DEBIT"] = "SEPA_DIRECT_DEBIT";
    PaymentMethod["SWISH"] = "SWISH";
})(PaymentMethod || (PaymentMethod = {}));
var PaymentMethodType;
(function(PaymentMethodType) {
    PaymentMethodType[/** @deprecated This will be removed and replaced by PaymentType.CARD */ "CREDITCARD"] = "CREDITCARD";
    PaymentMethodType[/** @deprecated This will be removed and replaced by PaymentType.SEPA */ "SEPA"] = "SEPA";
})(PaymentMethodType || (PaymentMethodType = {}));
var PaymentStatus;
(function(PaymentStatus) {
    PaymentStatus["DUE"] = "DUE";
    PaymentStatus["FAILED"] = "FAILED";
    PaymentStatus["PAID"] = "PAID";
})(PaymentStatus || (PaymentStatus = {}));
var PaymentType;
(function(PaymentType) {
    PaymentType["CARD"] = "CARD";
    PaymentType["IDEAL"] = "IDEAL";
    PaymentType["ONEY_3X"] = "ONEY_3X";
    PaymentType["ONEY_4X"] = "ONEY_4X";
    PaymentType["PAYPAL"] = "PAYPAL";
    PaymentType["SEPA"] = "SEPA";
})(PaymentType || (PaymentType = {}));
var PickupPointType;
(function(PickupPointType) {
    PickupPointType["FOREIGN_POST_OFFICE"] = "FOREIGN_POST_OFFICE";
    PickupPointType["LOCAL_LOCKERS"] = "LOCAL_LOCKERS";
    PickupPointType["LOCAL_POST_AGENCY"] = "LOCAL_POST_AGENCY";
    PickupPointType["LOCAL_POST_DISTRIBUTIION_CENTRE"] = "LOCAL_POST_DISTRIBUTIION_CENTRE";
    PickupPointType["LOCAL_POST_OFFICE"] = "LOCAL_POST_OFFICE";
    PickupPointType["LOCAL_SHOPS"] = "LOCAL_SHOPS";
    PickupPointType["LOCAL_SHOPS_ABROAD"] = "LOCAL_SHOPS_ABROAD";
})(PickupPointType || (PickupPointType = {}));
var PrescriptionGlassesOptionType;
(function(PrescriptionGlassesOptionType) {
    PrescriptionGlassesOptionType["BLUE_LIGHT_FILTER"] = "BLUE_LIGHT_FILTER";
    PrescriptionGlassesOptionType["MIRRORING"] = "MIRRORING";
    PrescriptionGlassesOptionType["PHOTOCHROMIC"] = "PHOTOCHROMIC";
    PrescriptionGlassesOptionType["POLARISING"] = "POLARISING";
})(PrescriptionGlassesOptionType || (PrescriptionGlassesOptionType = {}));
var PrescriptionType;
(function(PrescriptionType) {
    PrescriptionType["CONTACT_LENS_PRESCRIPTION"] = "CONTACT_LENS_PRESCRIPTION";
    PrescriptionType["HEARING_AID_PRESCRIPTION"] = "HEARING_AID_PRESCRIPTION";
    PrescriptionType["PRESCRIPTION_GLASSES_PRESCRIPTION"] = "PRESCRIPTION_GLASSES_PRESCRIPTION";
})(PrescriptionType || (PrescriptionType = {}));
var RecommendationDiscountType;
(function(RecommendationDiscountType) {
    RecommendationDiscountType["CART_DISCOUNT"] = "CART_DISCOUNT";
    RecommendationDiscountType["PRODUCT_DISCOUNT"] = "PRODUCT_DISCOUNT";
})(RecommendationDiscountType || (RecommendationDiscountType = {}));
var ReplacementFrequency;
(function(ReplacementFrequency) {
    ReplacementFrequency["BI_WEEKLY"] = "BI_WEEKLY";
    ReplacementFrequency["DAILY"] = "DAILY";
    ReplacementFrequency["MONTHLY"] = "MONTHLY";
})(ReplacementFrequency || (ReplacementFrequency = {}));
var ResultType;
(function(ResultType) {
    ResultType["AUTHORISED"] = "AUTHORISED";
    ResultType["CANCELLED"] = "CANCELLED";
    ResultType["ERROR"] = "ERROR";
    ResultType["REDIRECT"] = "REDIRECT";
    ResultType["REFUSED"] = "REFUSED";
    ResultType["THREE_DS_CHALLENGE"] = "THREE_DS_CHALLENGE";
})(ResultType || (ResultType = {}));
var RetrySubscriptionPaymentStatus;
(function(RetrySubscriptionPaymentStatus) {
    RetrySubscriptionPaymentStatus["PAYMENT_FAILED"] = "PAYMENT_FAILED";
    RetrySubscriptionPaymentStatus["PAYMENT_SUCCESS"] = "PAYMENT_SUCCESS";
})(RetrySubscriptionPaymentStatus || (RetrySubscriptionPaymentStatus = {}));
var RimType;
(function(RimType) {
    RimType["FULL_RIM"] = "FULL_RIM";
    RimType["HALF_RIM"] = "HALF_RIM";
    RimType["RIMLESS"] = "RIMLESS";
    RimType["UNKNOWN"] = "UNKNOWN";
})(RimType || (RimType = {}));
var SanitizationAction;
(function(SanitizationAction) {
    SanitizationAction["KeepExisting"] = "KeepExisting";
    SanitizationAction["Replace"] = "Replace";
})(SanitizationAction || (SanitizationAction = {}));
var ServicesNotAvailable;
(function(ServicesNotAvailable) {
    ServicesNotAvailable["APPOINTMENTS"] = "APPOINTMENTS";
    ServicesNotAvailable["CLICK_AND_RESERVE"] = "CLICK_AND_RESERVE";
    ServicesNotAvailable["EVENING_APPOINTMENTS"] = "EVENING_APPOINTMENTS";
    ServicesNotAvailable["OPTOMETRY"] = "OPTOMETRY";
    ServicesNotAvailable["PICK_UP_IN_STORE"] = "PICK_UP_IN_STORE";
})(ServicesNotAvailable || (ServicesNotAvailable = {}));
var SocialConnection;
(function(SocialConnection) {
    SocialConnection["GOOGLE"] = "GOOGLE";
})(SocialConnection || (SocialConnection = {}));
var SourceChannel;
(function(SourceChannel) {
    SourceChannel["C360"] = "C360";
    SourceChannel["ECOMMERCE"] = "ECOMMERCE";
})(SourceChannel || (SourceChannel = {}));
var Status;
(function(Status) {
    Status["OFFLINE"] = "OFFLINE";
    Status["ONLINE"] = "ONLINE";
})(Status || (Status = {}));
var StockIndicator;
(function(StockIndicator) {
    StockIndicator["IN_STOCK"] = "IN_STOCK";
    StockIndicator["LOW_STOCK"] = "LOW_STOCK";
    StockIndicator["OUT_OF_STOCK"] = "OUT_OF_STOCK";
    StockIndicator["UNKNOWN"] = "UNKNOWN";
})(StockIndicator || (StockIndicator = {}));
var StoreAppointmentDurationUnit;
(function(StoreAppointmentDurationUnit) {
    StoreAppointmentDurationUnit["MINUTES"] = "MINUTES";
})(StoreAppointmentDurationUnit || (StoreAppointmentDurationUnit = {}));
var StoreOfferType;
(function(StoreOfferType) {
    StoreOfferType["OFFER"] = "OFFER";
})(StoreOfferType || (StoreOfferType = {}));
var SubscriptionCancellationReason;
(function(SubscriptionCancellationReason) {
    SubscriptionCancellationReason["CS_BACKLOG"] = "CS_BACKLOG";
    SubscriptionCancellationReason["CS_CASH_PAYER"] = "CS_CASH_PAYER";
    SubscriptionCancellationReason["CS_CLINICAL_NON_TOLERANCE_BY_STORE"] = "CS_CLINICAL_NON_TOLERANCE_BY_STORE";
    SubscriptionCancellationReason["CS_COMPETITOR_CHEAPER"] = "CS_COMPETITOR_CHEAPER";
    SubscriptionCancellationReason["CS_DECEASED"] = "CS_DECEASED";
    SubscriptionCancellationReason["CS_DISCONTINUED_PRODUCT"] = "CS_DISCONTINUED_PRODUCT";
    SubscriptionCancellationReason["CS_DOES_NOT_FIT_MY_NEEDS"] = "CS_DOES_NOT_FIT_MY_NEEDS";
    SubscriptionCancellationReason["CS_INFREQUENT_USE"] = "CS_INFREQUENT_USE";
    SubscriptionCancellationReason["CS_LASER"] = "CS_LASER";
    SubscriptionCancellationReason["CS_LENSES_CHEAPER_IN_STORE"] = "CS_LENSES_CHEAPER_IN_STORE";
    SubscriptionCancellationReason["CS_LENS_QUALITY"] = "CS_LENS_QUALITY";
    SubscriptionCancellationReason["CS_MEDICAL"] = "CS_MEDICAL";
    SubscriptionCancellationReason["CS_MOVING_AWAY"] = "CS_MOVING_AWAY";
    SubscriptionCancellationReason["CS_OTHER"] = "CS_OTHER";
    SubscriptionCancellationReason["CS_PREFERRED_DELIVERY_FREQ_NOT_AVAILABLE"] = "CS_PREFERRED_DELIVERY_FREQ_NOT_AVAILABLE";
    SubscriptionCancellationReason["CS_PREFER_GLASSES"] = "CS_PREFER_GLASSES";
    SubscriptionCancellationReason["CS_PREGNANT"] = "CS_PREGNANT";
    SubscriptionCancellationReason["CS_PRICE_INCREASE"] = "CS_PRICE_INCREASE";
    SubscriptionCancellationReason["CS_REASON_DECLINED"] = "CS_REASON_DECLINED";
    SubscriptionCancellationReason["CS_SEASONAL_LENS_WEARER"] = "CS_SEASONAL_LENS_WEARER";
    SubscriptionCancellationReason["CS_SERVICE_GENERAL"] = "CS_SERVICE_GENERAL";
    SubscriptionCancellationReason["CS_SERVICE_STORE"] = "CS_SERVICE_STORE";
    SubscriptionCancellationReason["CS_STORE_REQUEST"] = "CS_STORE_REQUEST";
    SubscriptionCancellationReason["CS_TOO_EXPENSIVE"] = "CS_TOO_EXPENSIVE";
    SubscriptionCancellationReason["CS_TRYOUT_DIFFERENT_LENSES"] = "CS_TRYOUT_DIFFERENT_LENSES";
    SubscriptionCancellationReason["CS_WEARING_COMFORT"] = "CS_WEARING_COMFORT";
    SubscriptionCancellationReason["ECOM_BACKLOG"] = "ECOM_BACKLOG";
    SubscriptionCancellationReason["ECOM_COMPETITOR_CHEAPER"] = "ECOM_COMPETITOR_CHEAPER";
    SubscriptionCancellationReason["ECOM_DOES_NOT_FIT_MY_NEEDS"] = "ECOM_DOES_NOT_FIT_MY_NEEDS";
    SubscriptionCancellationReason["ECOM_INFREQUENT_USE"] = "ECOM_INFREQUENT_USE";
    SubscriptionCancellationReason["ECOM_LASER"] = "ECOM_LASER";
    SubscriptionCancellationReason["ECOM_MEDICAL"] = "ECOM_MEDICAL";
    SubscriptionCancellationReason["ECOM_MOVING_AWAY"] = "ECOM_MOVING_AWAY";
    SubscriptionCancellationReason["ECOM_OTHER"] = "ECOM_OTHER";
    SubscriptionCancellationReason["ECOM_PREFER_GLASSES"] = "ECOM_PREFER_GLASSES";
    SubscriptionCancellationReason["ECOM_TOO_EXPENSIVE"] = "ECOM_TOO_EXPENSIVE";
    SubscriptionCancellationReason["ECOM_TRYOUT_DIFFERENT_LENSES"] = "ECOM_TRYOUT_DIFFERENT_LENSES";
    SubscriptionCancellationReason["ECOM_WEARING_COMFORT"] = "ECOM_WEARING_COMFORT";
})(SubscriptionCancellationReason || (SubscriptionCancellationReason = {}));
var SubscriptionPaymentType;
(function(SubscriptionPaymentType) {
    SubscriptionPaymentType["CARD"] = "CARD";
    SubscriptionPaymentType["SEPA"] = "SEPA";
})(SubscriptionPaymentType || (SubscriptionPaymentType = {}));
var SubscriptionStatus;
(function(SubscriptionStatus) {
    SubscriptionStatus[/**
   * All good, the subscription is active
   * or
   * The subscription is created but the activation date is not yet passed
   */ "ACTIVE"] = "ACTIVE";
    SubscriptionStatus[/** The subscription is cancelled */ "CANCELLED"] = "CANCELLED";
    SubscriptionStatus[/** A locked subscription cannot be mutated. This might be the case for pending updates or due to system reasons */ "LOCKED"] = "LOCKED";
    SubscriptionStatus[/** For some reason the subscription is pending (this status should hardly occur) */ "PENDING"] = "PENDING";
    SubscriptionStatus[/** The subscription is on hold until it is brought back to active status */ "SUSPENDED"] = "SUSPENDED";
})(SubscriptionStatus || (SubscriptionStatus = {}));
var SubscriptionStatusReason;
(function(SubscriptionStatusReason) {
    SubscriptionStatusReason[/** SEPA payment setup failed. Customer must update payment details or choose a different payment method */ "SYS_BANK_ISSUE"] = "SYS_BANK_ISSUE";
    SubscriptionStatusReason[/** An internal error has meant the Product selected can not be delivered. Please contact the category team for review. */ "SYS_LRSV_ERROR"] = "SYS_LRSV_ERROR";
    SubscriptionStatusReason[/** Subscription has been cancelled due to non-payment.	SYS_N */ "SYS_NON_PAYMENT_CANCELLATION"] = "SYS_NON_PAYMENT_CANCELLATION";
    SubscriptionStatusReason[/** Payment for last invoice failed, customer must review payment method */ "SYS_NON_PAYMENT_ISSUE"] = "SYS_NON_PAYMENT_ISSUE";
    SubscriptionStatusReason[/** Customer has returned a product */ "SYS_PRODUCT_RETURNED"] = "SYS_PRODUCT_RETURNED";
    SubscriptionStatusReason[/** The delivery was rejected by a person at the delivery address */ "SYS_REJECTED_DELIVERY"] = "SYS_REJECTED_DELIVERY";
    SubscriptionStatusReason[/** The customer has requested a chargeback of his payment as allowed by SEPA payments */ "SYS_SEPA_CHARGEBACK"] = "SYS_SEPA_CHARGEBACK";
    SubscriptionStatusReason[/** Customer's subscription has been inactive for the maximum allowed period */ "SYS_SUSPENSION_LIMIT_EXCEEDED"] = "SYS_SUSPENSION_LIMIT_EXCEEDED";
    SubscriptionStatusReason[/** Product failed delivery, please review shipping address */ "SYS_UNABLE_TO_DELIVER"] = "SYS_UNABLE_TO_DELIVER";
})(SubscriptionStatusReason || (SubscriptionStatusReason = {}));
var SubscriptionSuspensionPeriod;
(function(SubscriptionSuspensionPeriod) {
    SubscriptionSuspensionPeriod["FIVE_MONTHS"] = "FIVE_MONTHS";
    SubscriptionSuspensionPeriod["FOUR_MONTHS"] = "FOUR_MONTHS";
    SubscriptionSuspensionPeriod["ONE_MONTH"] = "ONE_MONTH";
    SubscriptionSuspensionPeriod["SIX_MONTHS"] = "SIX_MONTHS";
    SubscriptionSuspensionPeriod["THREE_MONTHS"] = "THREE_MONTHS";
    SubscriptionSuspensionPeriod["TWO_MONTHS"] = "TWO_MONTHS";
})(SubscriptionSuspensionPeriod || (SubscriptionSuspensionPeriod = {}));
var SubscriptionSuspensionReason;
(function(SubscriptionSuspensionReason) {
    SubscriptionSuspensionReason["CS_BACKLOG"] = "CS_BACKLOG";
    SubscriptionSuspensionReason["CS_EMPLOYEE_DECISION"] = "CS_EMPLOYEE_DECISION";
    SubscriptionSuspensionReason["CS_NEW_TEST_COMING_UP"] = "CS_NEW_TEST_COMING_UP";
    SubscriptionSuspensionReason["CS_OTHER"] = "CS_OTHER";
    SubscriptionSuspensionReason["CS_OTHER_DELIVERY"] = "CS_OTHER_DELIVERY";
    SubscriptionSuspensionReason["CS_SEASONAL_USER"] = "CS_SEASONAL_USER";
    SubscriptionSuspensionReason["CS_TRAVEL"] = "CS_TRAVEL";
    SubscriptionSuspensionReason["CS_TRYING_NEW_PRODUCT"] = "CS_TRYING_NEW_PRODUCT";
    SubscriptionSuspensionReason["ECOM_BACKLOG"] = "ECOM_BACKLOG";
    SubscriptionSuspensionReason["ECOM_OTHER"] = "ECOM_OTHER";
    SubscriptionSuspensionReason["ECOM_OTHER_DELIVERY"] = "ECOM_OTHER_DELIVERY";
    SubscriptionSuspensionReason["ECOM_TRAVEL"] = "ECOM_TRAVEL";
})(SubscriptionSuspensionReason || (SubscriptionSuspensionReason = {}));
var TargetGender;
(function(TargetGender) {
    TargetGender["FEMALE"] = "FEMALE";
    TargetGender["MALE"] = "MALE";
    TargetGender["UNDEFINED"] = "UNDEFINED";
    TargetGender["UNISEX"] = "UNISEX";
})(TargetGender || (TargetGender = {}));
var UIType;
(function(UIType) {
    UIType["CHECKBOX"] = "CHECKBOX";
})(UIType || (UIType = {}));
var UsageLevel;
(function(UsageLevel) {
    UsageLevel["MORE_THAN_FIVE_DAYS_A_WEEK"] = "MORE_THAN_FIVE_DAYS_A_WEEK";
    UsageLevel["ONE_DAY_A_WEEK"] = "ONE_DAY_A_WEEK";
    UsageLevel["THREE_FOUR_DAYS_A_WEEK"] = "THREE_FOUR_DAYS_A_WEEK";
    UsageLevel["TWO_DAYS_A_WEEK"] = "TWO_DAYS_A_WEEK";
})(UsageLevel || (UsageLevel = {}));
var ValidationCode;
(function(ValidationCode) {
    ValidationCode["CONTAINS_MUTUALLY_EXCLUSIVE_PRODUCT_TYPES"] = "CONTAINS_MUTUALLY_EXCLUSIVE_PRODUCT_TYPES";
    ValidationCode["HAS_VALID_OPTICIAN_DETAILS"] = "HAS_VALID_OPTICIAN_DETAILS";
    ValidationCode["HAS_VALID_PRESCRIPTION_DATA_RANGES"] = "HAS_VALID_PRESCRIPTION_DATA_RANGES";
    ValidationCode["IS_IN_STOCK"] = "IS_IN_STOCK";
    ValidationCode["IS_VALID_AMOUNT_OF_MASTER_VARIANTS"] = "IS_VALID_AMOUNT_OF_MASTER_VARIANTS";
    ValidationCode["IS_VALID_CART_BY_PRESCRIPTION"] = "IS_VALID_CART_BY_PRESCRIPTION";
    ValidationCode["IS_VALID_EXPIRY_DATE"] = "IS_VALID_EXPIRY_DATE";
    ValidationCode["IS_VALID_QUANTITY"] = "IS_VALID_QUANTITY";
    ValidationCode["MAX_QUANTITY_PER_LINE_ITEM_TYPE"] = "MAX_QUANTITY_PER_LINE_ITEM_TYPE";
    ValidationCode["MAX_QUANTITY_PER_PRODUCT_TYPE"] = "MAX_QUANTITY_PER_PRODUCT_TYPE";
    ValidationCode["MAX_VARIANTS_PER_PRODUCT_TYPE"] = "MAX_VARIANTS_PER_PRODUCT_TYPE";
    ValidationCode["ONE_LENS_TYPE_PER_EYE"] = "ONE_LENS_TYPE_PER_EYE";
    ValidationCode["PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT"] = "PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT";
    ValidationCode["PRODUCTS_ARE_PUBLISHED"] = "PRODUCTS_ARE_PUBLISHED";
    ValidationCode["REQUIRED_PRODUCT_TYPES_ARE_IN_BUNDLE"] = "REQUIRED_PRODUCT_TYPES_ARE_IN_BUNDLE";
    ValidationCode["VALID_CART_BY_PRODUCT_TYPES"] = "VALID_CART_BY_PRODUCT_TYPES";
    ValidationCode["VALID_RELATIONS_BUNDLE_PRODUCTS"] = "VALID_RELATIONS_BUNDLE_PRODUCTS";
})(ValidationCode || (ValidationCode = {}));
var VisualAcuityType;
(function(VisualAcuityType) {
    VisualAcuityType["aided"] = "aided";
    VisualAcuityType["unaided"] = "unaided";
})(VisualAcuityType || (VisualAcuityType = {}));
var VoucherDiscountType;
(function(VoucherDiscountType) {
    VoucherDiscountType["AMOUNT"] = "AMOUNT";
    VoucherDiscountType["FIXED"] = "FIXED";
    VoucherDiscountType["PERCENT"] = "PERCENT";
    VoucherDiscountType["UNIT"] = "UNIT";
})(VoucherDiscountType || (VoucherDiscountType = {}));
var VoucherEffectType;
(function(VoucherEffectType) {
    VoucherEffectType["ADD_MANY_ITEMS"] = "ADD_MANY_ITEMS";
    VoucherEffectType["ADD_MISSING_ITEMS"] = "ADD_MISSING_ITEMS";
    VoucherEffectType["ADD_NEW_ITEMS"] = "ADD_NEW_ITEMS";
    VoucherEffectType["APPLY_TO_ITEMS"] = "APPLY_TO_ITEMS";
    VoucherEffectType["APPLY_TO_ITEMS_PROPORTIONALLY"] = "APPLY_TO_ITEMS_PROPORTIONALLY";
    VoucherEffectType["APPLY_TO_ITEMS_PROPORTIONALLY_BY_QUANTITY"] = "APPLY_TO_ITEMS_PROPORTIONALLY_BY_QUANTITY";
    VoucherEffectType["APPLY_TO_ORDER"] = "APPLY_TO_ORDER";
})(VoucherEffectType || (VoucherEffectType = {}));
var VoucherState;
(function(VoucherState) {
    VoucherState["ApplicationStoppedByPreviousDiscount"] = "ApplicationStoppedByPreviousDiscount";
    VoucherState["DoesNotMatchCart"] = "DoesNotMatchCart";
    VoucherState["MatchesCart"] = "MatchesCart";
    VoucherState["MaxApplicationReached"] = "MaxApplicationReached";
    VoucherState["NotActive"] = "NotActive";
    VoucherState["NotValid"] = "NotValid";
})(VoucherState || (VoucherState = {}));

var generatedTypes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get AccessoryType () { return AccessoryType; },
  get AccountOrigination () { return AccountOrigination; },
  get AccountReason () { return AccountReason; },
  get AccountVerificationMethod () { return AccountVerificationMethod; },
  get AddressType () { return AddressType; },
  get AppointmentCommunicationChannel () { return AppointmentCommunicationChannel; },
  get BillingFrequency () { return BillingFrequency; },
  get BundleLineItemProductType () { return BundleLineItemProductType; },
  get BundleLineItemType () { return BundleLineItemType; },
  get CLCareType () { return CLCareType; },
  get CacheControlScope () { return CacheControlScope; },
  get CartCustomerType () { return CartCustomerType; },
  get CartState () { return CartState; },
  get Channel () { return Channel; },
  get ChannelAvailability () { return ChannelAvailability; },
  get ChannelType () { return ChannelType; },
  get CheckoutCustomerType () { return CheckoutCustomerType; },
  get CheckoutState () { return CheckoutState; },
  get ColorType () { return ColorType; },
  get CommunicationChannel () { return CommunicationChannel; },
  get CreateAccountTargetAction () { return CreateAccountTargetAction; },
  get CustomerType () { return CustomerType; },
  get DayOfWeek () { return DayOfWeek; },
  get DeliveryFrequency () { return DeliveryFrequency; },
  get DeliveryStatus () { return DeliveryStatus; },
  get DiscountProductType () { return DiscountProductType; },
  get DiscountType () { return DiscountType; },
  get DiscountValueType () { return DiscountValueType; },
  get Dominance () { return Dominance; },
  get EaseePrescriptionType () { return EaseePrescriptionType; },
  get EyeSide () { return EyeSide; },
  get FaceShape () { return FaceShape; },
  get FilterFieldNames () { return FilterFieldNames; },
  get FrameShape () { return FrameShape; },
  get FrameSize () { return FrameSize; },
  get FrameType () { return FrameType; },
  get HttpMethod () { return HttpMethod; },
  get LineItemType () { return LineItemType; },
  get MaterialGroup () { return MaterialGroup; },
  get NormalizePrice () { return NormalizePrice; },
  get OfferingAction () { return OfferingAction; },
  get OrderDeliveryMethod () { return OrderDeliveryMethod; },
  get OrderPaymentMethod () { return OrderPaymentMethod; },
  get OrderPaymentStatus () { return OrderPaymentStatus; },
  get OrderShipmentStatus () { return OrderShipmentStatus; },
  get OrderStatus () { return OrderStatus; },
  get PSP () { return PSP; },
  get PaymentMethod () { return PaymentMethod; },
  get PaymentMethodType () { return PaymentMethodType; },
  get PaymentStatus () { return PaymentStatus; },
  get PaymentType () { return PaymentType; },
  get PickupPointType () { return PickupPointType; },
  get PrescriptionGlassesOptionType () { return PrescriptionGlassesOptionType; },
  get PrescriptionType () { return PrescriptionType; },
  get RecommendationDiscountType () { return RecommendationDiscountType; },
  get ReplacementFrequency () { return ReplacementFrequency; },
  get ResultType () { return ResultType; },
  get RetrySubscriptionPaymentStatus () { return RetrySubscriptionPaymentStatus; },
  get RimType () { return RimType; },
  get SanitizationAction () { return SanitizationAction; },
  get ServicesNotAvailable () { return ServicesNotAvailable; },
  get SocialConnection () { return SocialConnection; },
  get SourceChannel () { return SourceChannel; },
  get Status () { return Status; },
  get StockIndicator () { return StockIndicator; },
  get StoreAppointmentDurationUnit () { return StoreAppointmentDurationUnit; },
  get StoreOfferType () { return StoreOfferType; },
  get SubscriptionCancellationReason () { return SubscriptionCancellationReason; },
  get SubscriptionPaymentType () { return SubscriptionPaymentType; },
  get SubscriptionStatus () { return SubscriptionStatus; },
  get SubscriptionStatusReason () { return SubscriptionStatusReason; },
  get SubscriptionSuspensionPeriod () { return SubscriptionSuspensionPeriod; },
  get SubscriptionSuspensionReason () { return SubscriptionSuspensionReason; },
  get TargetGender () { return TargetGender; },
  get UIType () { return UIType; },
  get UsageLevel () { return UsageLevel; },
  get ValidationCode () { return ValidationCode; },
  get VisualAcuityType () { return VisualAcuityType; },
  get VoucherDiscountType () { return VoucherDiscountType; },
  get VoucherEffectType () { return VoucherEffectType; },
  get VoucherState () { return VoucherState; }
});

const getOperationInfo = (query)=>{
    const operationNode = query.definitions[0];
    let name = 'anonymous';
    // Fallback name when the operation has been malformed but still send to the BE
    let type = operationNode.kind;
    if (operationNode.kind === 'OperationDefinition') {
        type = operationNode.operation;
        if (operationNode.name) {
            name = operationNode.name.value;
        }
    }
    return [
        type,
        name
    ];
};
const handleError = (query, error)=>{
    const [type, name] = getOperationInfo(query);
    console.error(`[${type}] - ${name} - error`, {
        error
    });
};

const useLazyQuery = (query, options)=>{
    const [executable, result] = useLazyQuery$1(query, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(query, error);
        }
    }, [
        error,
        query
    ]);
    return [
        executable,
        result
    ];
};

const useMutation = (mutation, options)=>{
    const [executable, result] = useMutation$1(mutation, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(mutation, error);
        }
    }, [
        error,
        mutation
    ]);
    return [
        executable,
        result
    ];
};

const useQuery = (query, options)=>{
    const result = useQuery$1(query, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(query, error);
        }
    }, [
        error,
        query
    ]);
    return result;
};

export { DebugMockedProvider, generatedTypes as Types, formatClientVersionInfo, getGraphQLErrorCode, initApolloClient, useLazyQuery, useMutation, useQuery, wait };
//# sourceMappingURL=index.js.map
